import { useCallback, useMemo, type ChangeEvent } from "react";
import { Flex, TextField } from "@radix-ui/themes";
import { Tabs } from "@/v2/components/tabs/Tabs";
import Select, { type SelectOption } from "@/components/shared/select";
import Switch from "@/components/shared/inputs/switch";
import { MultiSelectDropdown, type MultiSelectOption } from "../../../knowledge-hub-channel/knowledge-hub/parts/drawer/MultiSelectDropdown";
import { CollapsibleFieldList } from "../../../parts/CollapsibleFieldList";
import { FieldGroup, OptionalFieldGroup } from "../../../parts/FieldGroup";
import type { layoutSupportTicketsStyle } from "@/components/quack-chat/core/entities/ChatSettingsV2";
import {
  SUPPORT_TICKET_TABS,
  type SupportTicketsTab,
} from "../constants/supportTicketsTabs";

interface SupportTicketsSettingsProps {
  enabled: boolean;
  style: layoutSupportTicketsStyle;
  statusesToDisplay: string[];
  endUserTicketName: string;
  currentTab: SupportTicketsTab;
  statusOptions: MultiSelectOption[];
  isSalesforceTicketSystemConnected: boolean;
  allowUserReplyToTicket: boolean;
  onEnabledChange: (nextValue: boolean) => void;
  onStyleChange: (value: layoutSupportTicketsStyle) => void;
  onStatusesChange: (value: string[]) => void;
  onEndUserTicketNameTabChange: (value: SupportTicketsTab) => void;
  onEndUserTicketNameChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onAllowRepliesChange: (nextValue: boolean) => void;
}

export function SupportTicketsSettings({
  enabled,
  style,
  statusesToDisplay,
  endUserTicketName,
  currentTab,
  statusOptions,
  isSalesforceTicketSystemConnected,
  allowUserReplyToTicket,
  onEnabledChange,
  onStyleChange,
  onStatusesChange,
  onEndUserTicketNameTabChange,
  onEndUserTicketNameChange,
  onAllowRepliesChange,
}: SupportTicketsSettingsProps) {
  const tabs = useMemo(
    () => SUPPORT_TICKET_TABS.map((tab) => ({ value: tab, label: tab })),
    []
  );

  const styleOptions = useMemo<SelectOption[]>(
    () => [
      { value: "list", label: "List" },
      { value: "carousel", label: "Carousel" },
    ],
    []
  );

  const handleTabChange = useCallback(
    (value: string) => {
      onEndUserTicketNameTabChange(value as SupportTicketsTab);
    },
    [onEndUserTicketNameTabChange]
  );

  const handleStyleChange = useCallback(
    (value: string) => {
      onStyleChange(value as layoutSupportTicketsStyle);
    },
    [onStyleChange]
  );

  return (
    <CollapsibleFieldList title={"Support Tickets"} defaultOpen>
      <OptionalFieldGroup
        label={"Show Support Tickets"}
        description={"Surface tickets on Quack Home and the tickets list."}
        enabled={enabled}
        toggleEnabled={onEnabledChange}
      >
        <Flex width="100%" direction="column">
          <FieldGroup horizontal description={"Style"}>
            <Select
              value={style}
              onChange={handleStyleChange}
              options={styleOptions}
              disabled={!enabled}
            />
          </FieldGroup>

          <FieldGroup horizontal description={"Statuses to display on the page"}>
            <MultiSelectDropdown
              value={statusesToDisplay}
              onChange={onStatusesChange}
              options={statusOptions}
              placeholder="Select statuses..."
              disabled={!enabled}
            />
          </FieldGroup>

          <FieldGroup horizontal description={"End user ticket name"}>
            <Flex direction="column" gap="2" width="100%">
              <Tabs
                fullWidth
                variant="data"
                value={currentTab}
                onValueChange={handleTabChange}
                tabs={tabs}
              />
              {currentTab === "Custom" && (
                <TextField.Root
                  value={endUserTicketName}
                  placeholder="Tickets"
                  onChange={onEndUserTicketNameChange}
                  variant="soft"
                  color="gray"
                  disabled={!enabled}
                />
              )}
            </Flex>
          </FieldGroup>

          {isSalesforceTicketSystemConnected && (
            <FieldGroup
              horizontal
              label={"Allow user replies"}
              description="Let signed-in users reply to their tickets."
            >
              <Flex align="center" gap="2">
                <Switch
                  value={allowUserReplyToTicket}
                  onChange={onAllowRepliesChange}
                  disabled={!enabled}
                />
              </Flex>
            </FieldGroup>
          )}
        </Flex>
      </OptionalFieldGroup>
    </CollapsibleFieldList>
  );
}
